
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import { useLikes } from '../../../context/LikesContext';

interface Artist {
  id: string;
  slug: string;
  name: string;
  avatar: string;
  city: string;
  lanes: string[];
  verified: boolean;
  likes: number;
  releasesCount: number;
  featuresCount: number;
  lastRelease: string;
  hasNewRelease: boolean;
  hasMemberships: boolean;
  roles: string[];
  highlightTrack?: {
    title: string;
    url: string;
    duration: number;
  };
}

interface ArtistCardProps {
  artist: Artist;
  isPlaying: boolean;
  onPreview: () => void;
}

export default function ArtistCard({
  artist,
  isPlaying,
  onPreview
}: ArtistCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [likesCount, setLikesCount] = useState(artist.likes);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isArtistLiked, setArtistLike } = useLikes();
  const numericId = Number(artist.id);
  const isLiked = isArtistLiked(numericId);

  useEffect(() => {
    setLikesCount(artist.likes);
  }, [artist.id, artist.likes]);

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview();
  };

  const handleCardClick = () => {
    navigate(`/artists/${artist.slug}`);
  };

  const formatLikes = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const showComingSoon = artist.releasesCount === 0 && artist.featuresCount === 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      <div className="relative aspect-square">
        <img
          src={artist.avatar}
          alt={artist.name}
          className="w-full h-full object-cover"
        />
        
        {/* New Release Badge */}
        {artist.hasNewRelease && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              New release
            </span>
          </div>
        )}

        {/* Hover Actions */}
        <div className={`absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center gap-3 transition-opacity duration-200 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          {/* Preview Button */}
          {artist.highlightTrack && (
            <button
              onClick={handlePreview}
              className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              {isPlaying ? (
                <i className="ri-pause-fill text-gray-900 text-xl"></i>
              ) : (
                <i className="ri-play-fill text-gray-900 text-xl ml-1"></i>
              )}
            </button>
          )}

          {/* Follow Button */}
          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const nextCount = await setArtistLike(numericId, !isLiked);
                setLikesCount(nextCount);
              } catch (error) {
                if (error instanceof Error && error.message === 'AUTH_REQUIRED') {
                  addToast({
                    message: 'Create a free account to like this artist.',
                    actionLabel: 'Create account',
                    actionHref: '/signup',
                  });
                  return;
                }
                addToast({
                  message: 'Unable to update your like. Please try again.',
                  variant: 'error',
                });
              }
            }}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isLiked ? 'bg-rose-500 text-white' : 'bg-white text-gray-900 hover:bg-gray-100'
            }`}
            aria-pressed={isLiked}
            aria-label={isLiked ? 'Unlike artist' : 'Like artist'}
          >
            <i className={isLiked ? 'ri-heart-fill text-xl' : 'ri-heart-line text-xl'} />
          </button>
        </div>

        {/* Playing Indicator */}
        {isPlaying && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1">
            <div className="flex items-center gap-1">
              <div className="w-1 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
              <div className="w-1 h-4 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-2 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">
              {artist.name}
            </h3>
            <p className="text-xs text-gray-500 mb-2">{artist.city}</p>
          </div>
        </div>

        {/* Lane Chips */}
        <div className="flex flex-wrap gap-1 mb-3">
          {artist.lanes.slice(0, 2).map((lane) => (
            <span
              key={lane}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
            >
              {lane}
            </span>
          ))}
        </div>

        {/* Micro Metrics */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{formatLikes(likesCount)} likes</span>
          {showComingSoon && <span>{artist.lastRelease}</span>}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          <span>{artist.releasesCount} releases</span>
          <span className="text-gray-300">•</span>
          <span>{artist.featuresCount} features</span>
        </div>

        {/* Feature Indicators */}
        <div className="flex items-center gap-2 mt-2">
          {artist.hasMemberships && (
            <span className="inline-flex items-center text-xs text-emerald-600">
              <i className="ri-vip-crown-line mr-1"></i>
              Member tiers
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
